/**
 * Site DISPLAY COPY (PL + EN).
 *
 * The frozen @mb-group/i18n package holds only shared UI chrome, so the
 * marketing wording of the approved mock-up lives here. PL is the source of
 * truth (the mock-up is Polish); EN is a translation of the same structure.
 *
 * Editorial content proper (services, categories, articles, case studies) comes
 * from the CMS via GraphQL — see data/gateway.ts. This file is only the frame
 * around it: headings, labels, button text and static explanatory copy.
 */
import type { Locale } from "@mb-group/shared-types";
import { plCopy } from "./copy-pl.js";
import { enCopy } from "./copy-en.js";
import { typesetContent } from "../data/typography.js";

export interface Stat {
	value: string;
	label: string;
}

export interface Labelled {
	title: string;
	desc: string;
}

export interface ProcessStep {
	n: string;
	title: string;
	desc: string;
}

export interface SiteCopy {
	brand: {
		name: string;
		tagline: string;
		quote: string;
		blurb: string;
		mockupNote: string;
	};
	nav: {
		services: string;
		industries: string;
		cases: string;
		knowledge: string;
		about: string;
		contact: string;
		consult: string;
		search: string;
		menu: string;
		close: string;
		megaEyebrow: string;
		fullPortfolio: string;
		start: string;
	};
	home: {
		heroEyebrow: string;
		heroTitle: string;
		heroLead: string;
		heroSearchPlaceholder: string;
		heroCtaPrimary: string;
		heroCtaSecondary: string;
		heroStats: Stat[];
		heroFigureCaption: string;
		heroFigureAlt: string;
		ticker: string[];
		videoEyebrow: string;
		videoTitle: string;
		videoLead: string;
		videoStats: Stat[];
		videoPlay: string;
		videoPause: string;
		videoMuteOn: string;
		videoMuteOff: string;
		videoMore: string;
		navigatorEyebrow: string;
		navigatorTitle: string;
		navigatorLead: string;
		navigatorBadge: string;
		navigatorConsult: string;
		navigatorStatLabel: string;
		proofEyebrow: string;
		proofTitle: string;
		proofLead: string;
		proofFacts: Stat[];
		industriesTitle: string;
		industriesAll: string;
		magnetsEyebrow: string;
		magnetsTitle: string;
		magnetsLead: string;
		magnetsAll: string;
		ctaEyebrow: string;
		ctaTitle: string;
		ctaLead: string;
		ctaFacts: Stat[];
	};
	newsletter: {
		title: string;
		desc: string;
		emailPlaceholder: string;
		submit: string;
		doneTitle: string;
		doneDesc: string;
		kind: string;
		tag: string;
	};
	quickForm: {
		title: string;
		desc: string;
		name: string;
		phone: string;
		slotLabel: string;
		slots: string[];
		submit: string;
		mailInstead: string;
		sentTitle: string;
		sentDesc: string;
		sendAnother: string;
	};
	services: {
		title: string;
		lead: string;
		viewService: string;
		inCategory: string;
	};
	serviceDetail: {
		ask: string;
		allServices: string;
		scopeEyebrow: string;
		scopeTitle: string;
		groupsEyebrow: string;
		groupsTitle: string;
		notListedTitle: string;
		notListedDesc: string;
		notListedCta: string;
		processEyebrow: string;
		processTitle: string;
		process: ProcessStep[];
		relatedTitle: string;
		relatedAll: string;
	};
	about: {
		eyebrow: string;
		title: string;
		lead: string;
		bodyTitle: string;
		bodyParagraphs: string[];
		pillarsTitle: string;
	};
	industries: {
		title: string;
		lead: string;
	};
	cases: {
		title: string;
		lead: string;
	};
	knowledge: {
		title: string;
		lead: string;
		read: string;
	};
	article: {
		ctaTitle: string;
		ctaDesc: string;
		ctaConsult: string;
		ctaBack: string;
	};
	contact: {
		title: string;
		lead: string;
		points: string[];
		formTitle: string;
		name: string;
		company: string;
		email: string;
		categoryPlaceholder: string;
		message: string;
		submit: string;
		privacyNote: string;
		sentTitle: string;
		sentDesc: string;
		sendAnother: string;
	};
	assistant: {
		title: string;
		subtitle: string;
		placeholder: string;
		frequent: string;
		prompts: string[];
		recommendation: string;
		/** "{service}" and "{category}" placeholders. */
		answerFound: string;
		answerEmpty: string;
		privacyNote: string;
		expertCta: string;
		openLabel: string;
		resultsLabel: string;
	};
	gate: {
		email: string;
		company: string;
		privacyNote: string;
		close: string;
	};
	/** Resources and webinars pages (formerly inline in views/resources.ts). */
	resources: {
		title: string;
		webinarsTitle: string;
		upcoming: string;
		recording: string;
		noWebinars: string;
	};
	/** Search page (formerly inline in views/search.ts). */
	search: {
		emptyHint: string;
		/** `{query}` and `{total}` are filled in by the view. */
		resultsCount: string;
	};
	/** Draft preview without an editor role (formerly inline in views/status.ts). */
	preview: {
		title: string;
		desc: string;
		signIn: string;
		/** Signed in, but the role may not read drafts. */
		forbidden: string;
	};
	/** Editor panel at /panel: sign-in with the Strapi account, then what it unlocks. */
	panel: {
		title: string;
		signInLead: string;
		email: string;
		password: string;
		submit: string;
		submitting: string;
		errorInvalid: string;
		errorRateLimited: string;
		errorUnavailable: string;
		signedInAs: string;
		rolesTitle: string;
		noEditorRole: string;
		cmsTitle: string;
		cmsDesc: string;
		cmsCta: string;
		previewTitle: string;
		previewDesc: string;
		signOut: string;
		roleNames: Record<string, string>;
	};
	footer: {
		servicesTitle: string;
		groupTitle: string;
		contactTitle: string;
		locations: string;
		rights: string;
	};
	status: {
		notFoundTitle: string;
		notFoundDesc: string;
		notFoundCta: string;
		errorTitle: string;
		errorDesc: string;
		retry: string;
	};
	demo: {
		bannerTitle: string;
		bannerBody: string;
	};
	a11y: {
		mainNav: string;
		breadcrumb: string;
		servicesMenu: string;
	};
}

// Hard spaces for the Polish dictionary, once at export (see data/typography.ts).
// copy-pl.ts itself keeps plain spaces; EN is returned unchanged by the adapter.
const COPY: Record<Locale, SiteCopy> = {
	pl: typesetContent(plCopy, "pl"),
	en: typesetContent(enCopy, "en"),
};

export function getCopy(locale: Locale): SiteCopy {
	return COPY[locale] ?? COPY.pl;
}
